import type { PartnerPush } from '@nac2s/shared'
import { useEffect, useState } from 'react'
import { Knopf } from '@/teile/Knopf'
import { Status } from '@/teile/Status'
import { Fehler } from '@/teile/Tabelle'
import { ApiError } from '../api/client.js'
import { ladePush, pushErzeugen } from '../api/partner.js'

/**
 * Reiter „Sipgate Push-API" — die Push-Adresse des Kunden (ADR-0011).
 *
 * Die Adresse gilt fuer das ganze sipgate-Konto des Kunden und traegt sein
 * Geheimnis. Ein sipgate-Administrator (der Partner) traegt sie in der
 * sipgate-Web-App ein — deshalb steht sie hier und nicht beim Anwender.
 */
export function SipgatePushApi({ host, darfTechnik }: { host: string; darfTechnik: boolean }) {
  const [stand, setStand] = useState<PartnerPush | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [kopiert, setKopiert] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    if (!darfTechnik) return
    let aktiv = true
    ladePush(host)
      .then((s) => {
        if (aktiv) setStand(s)
      })
      .catch((f: unknown) => {
        if (aktiv) setFehler(satzZu(f))
      })
    return () => {
      aktiv = false
    }
  }, [host, darfTechnik])

  if (!darfTechnik) {
    return (
      <p className="text-[13px] text-tertiary">
        Die Push-Adresse sehen und erzeugen nur Technik und Admin — sie enthält das Geheimnis des
        Kunden.
      </p>
    )
  }

  async function erzeugen() {
    setLaeuft(true)
    setFehler(null)
    try {
      setStand(await pushErzeugen(host))
    } catch (f: unknown) {
      setFehler(satzZu(f))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <section className="flex max-w-3xl flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <h2 className="font-display text-base font-bold text-primary">Anrufereignisse</h2>
        {stand ? (
          stand.eingerichtet ? (
            <Status ton="gut">Adresse erzeugt</Status>
          ) : (
            <Status ton="wartet">noch nicht erzeugt</Status>
          )
        ) : null}
      </div>
      <p className="text-[13px] text-tertiary">
        sipgate meldet dem Dienst, wann ein Anruf klingelt, angenommen und beendet wird. Dafür trägt
        ein Administrator des sipgate-Kontos diese Adresse in der sipgate-Web-App unter sipgate.io
        als URL für ein- und ausgehende Anrufe ein. Sie enthält das Geheimnis des Kunden — nur an
        sipgate weitergeben.
      </p>
      {fehler ? <Fehler>{fehler}</Fehler> : null}
      {stand?.adresse ? (
        <div className="flex flex-wrap items-center gap-2.5">
          <code className="max-w-full rounded-lg border border-secondary bg-primary px-3 py-2 font-mono text-[12.5px] break-all text-primary select-all">
            {stand.adresse}
          </code>
          <Knopf
            onClick={() => {
              void navigator.clipboard?.writeText(stand.adresse ?? '').then(() => setKopiert(true))
            }}
          >
            {kopiert ? 'Kopiert' : 'Kopieren'}
          </Knopf>
        </div>
      ) : stand ? (
        <div>
          <Knopf stark disabled={laeuft} onClick={() => void erzeugen()}>
            {laeuft ? 'Wird erzeugt …' : 'Push-Adresse erzeugen'}
          </Knopf>
        </div>
      ) : null}
    </section>
  )
}

function satzZu(fehler: unknown): string {
  return fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler'
}
